import React from 'react'

import { compose, withHandlers, withProps, withState } from 'recompose'
import styled, { css } from 'styled-components'

import CheckIcon from 'assets/icons/check.svg'
import DollarIcon from 'assets/icons/dollar.svg'
import SignUpIcon from 'assets/icons/person.svg'
import PieChartIcon from 'assets/icons/pie-chart.svg'
import LaterLogo from 'assets/images/later-video.png'
import Button from 'components/Button'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import PlayButton from 'components/PlayButton'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import platform from 'components/Trackers/platform'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { Title } from 'components/Typography'
import VideoModal from 'components/VideoModal'
import { style } from 'theme'
import { FlowRoute } from 'utils/FlowProvider/types'

import { TestIDs } from '../../../../test/utils/testIds'

type Props = {
  handleClick: () => void
  toggleVideo: () => void
  videoModalOpen: boolean
  setVideoModalOpen: (videoModalOpen: boolean) => void
  header: string
  text: string
  videoSrc: string
} & HeroTextProps &
  RemoteStringsProps

export const enhance = compose<Props, {}>(
  withHeroText,
  withRemoteStrings,
  withProps(({ getRemoteString, heroText }) => {
    return {
      header: getRemoteString('welcome_header_v3', heroText.header),
      text: getRemoteString('welcome_text_v3', heroText.text),
      videoSrc:
        platform === 'desktop'
          ? getRemoteString('welcome_video_desktop_v3', null) ||
            require('assets/videos/acorns-later-web.mp4')
          : getRemoteString('welcome_video_mobile_v3', null) ||
            require('assets/videos/acorns-later-mobile.mp4'),
    }
  }),

  withState('videoModalOpen', 'setVideoModalOpen', false),
  withHandlers({
    handleClick: ({ route }) => () => {
      route({ from: FlowRoute.Welcome, action: 'continue' })
    },
    toggleVideo: ({ setVideoModalOpen, videoModalOpen }) => () => {
      setVideoModalOpen(!videoModalOpen)
    },
  }),
)

const GetStartedButton = withClickTrack<any, any>(TRACKER_IDS.WELCOME.NEXT_BUTTON)(Button)

const Container = styled.div`
  text-align: center;
`

const VideoContainer = styled.div<{ bgImage: string }>(
  ({ bgImage }) => `
  background: url(${bgImage}) no-repeat center;
  background-size: cover;
  border-radius: 6px;
  box-shadow: 0px 0px 20px -2px rgba(0, 0, 0, 0.13);
  height: 180px;
  margin: 10px auto 30px;
  max-width: 335px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-top: -10px;
  }
`,
)

const VideoHeader = styled.div`
  color: ${style(theme => theme.colors.slate)};
  font-size: 22px;
  font-weight: 500;
  line-height: 30px;
  margin-bottom: 30px;
`

const Header = styled(Title)`
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  margin: 0 auto 5px;
`

const Text = styled.div`
  color: ${style(theme => theme.colors.slate)};
  letter-spacing: 0px;
  line-height: 22px;
  max-width: 450px;
  margin: 0 auto;
  margin-bottom: 30px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto 60px;
  width: 100%;
  max-width: 214px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 30px;
  }
`

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  height: 24px;
  margin-bottom: 20px;
  align-items: center;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const ListItemImage = styled.object<{ wider?: boolean }>`
  display: block;
  height: auto;
  margin-left: 3px;
  margin-right: 13px;
  width: 24px;

  ${props =>
    props.wider &&
    css`
      width: 30px;
      margin-left: 0;
      margin-right: 10px;
    `}
`

const ListItemFallbackImage = styled.img<{ wider?: boolean }>`
  display: block;
  height: auto;
  margin-left: 3px;
  margin-right: 13px;
  width: 24px;

  ${props =>
    props.wider &&
    css`
      width: 30px;
      margin-left: 0;
      margin-right: 10px;
    `}
`

const ListItemText = styled.div`
  color: ${style(theme => theme.colors.slate)};
  flex: 1;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0;
  line-height: 22px;
  text-align: left;
`

export const LaterLanding: React.SFC<Props> = ({
  handleClick,
  header,
  text,
  getRemoteString,
  toggleVideo,
  videoModalOpen,
  videoSrc,
}) => (
  <>
    <Nav />
    <Layout>
      <Container>
        <VideoContainer bgImage={getRemoteString('welcome_image_v3', LaterLogo, true)}>
          <VideoHeader data-testid={TestIDs.WELCOME.LATER.VIDEO_HEADER}>
            {getRemoteString('welcome_video_header_v3', 'Welcome to Acorns Later')}
          </VideoHeader>
          <PlayButton handleClick={toggleVideo} />
        </VideoContainer>
        <Header data-testid={TestIDs.WELCOME.LATER.HEADER}>{header}</Header>
        <Text data-testid={TestIDs.WELCOME.LATER.TEXT}>{text}</Text>
        <List>
          <ListItem>
            <ListItemImage
              data={getRemoteString('welcome_list_earn_icon_v3', DollarIcon, true)}
              type="image/svg+xml"
              wider
            >
              <ListItemFallbackImage src={DollarIcon} wider />
            </ListItemImage>
            <ListItemText>
              {getRemoteString('welcome_list_earn_v3', 'Earn potential tax advantages')}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemImage
              data={getRemoteString('welcome_list_signup_icon_v3', SignUpIcon, true)}
              type="image/svg+xml"
            >
              <ListItemFallbackImage src={SignUpIcon} />
            </ListItemImage>
            <ListItemText>
              {getRemoteString('welcome_list_signup_v3', 'Sign up in minutes')}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemImage
              data={getRemoteString('welcome_list_set_icon_v3', CheckIcon, true)}
              type="image/svg+xml"
            >
              <ListItemFallbackImage src={CheckIcon} />
            </ListItemImage>
            <ListItemText>
              {getRemoteString('welcome_list_set_v3', 'Set it and forget it')}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemImage
              data={getRemoteString('welcome_list_invest_icon_v3', PieChartIcon, true)}
              type="image/svg+xml"
            >
              <ListItemFallbackImage src={PieChartIcon} />
            </ListItemImage>
            <ListItemText>
              {getRemoteString('welcome_list_invest_v3', 'Built-in investment account')}
            </ListItemText>
          </ListItem>
        </List>
        <Separator />
        <GetStartedButton onPress={handleClick} data-testid={TestIDs.WELCOME.LATER.SUBMIT_CTA}>
          {getRemoteString('welcome_cta_v3', 'Get started')}
        </GetStartedButton>
      </Container>
      <VideoModal
        isOpen={videoModalOpen}
        onRequestClose={toggleVideo}
        onEnded={toggleVideo}
        videoSrc={videoSrc}
      />
    </Layout>
  </>
)

export default enhance(LaterLanding)
